import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { WORDS } from "../lib/tokiPonaData";
import {
  CONSONANTS,
  VOWELS,
  ILLEGAL_SYLLABLES,
  isValidWord,
  legalSyllables,
  strokeToLatin,
  syllableDistractors,
  wordDistractors,
  shuffleWith,
} from "../lib/sitelenNasin";
import {
  SyllableGlyph,
  WordGlyph,
  ScriptText,
  GlyphCard,
} from "../components/sitelenNasin/Glyphs";
import "../styles/sitelenNasin.css";

// Friendly names for each consonant gesture and vowel ending, used by the
// anatomy panel so a syllable can be read as "gesture + ending".
const CONS_NAMES = {
  p: ["gate", "across the top, then drop down the right side"],
  t: ["slash", "one diagonal cut from top-left to bottom-right"],
  k: ["peak", "up to a point and back down"],
  s: ["zigzag", "across, slice back down, across again"],
  m: ["hill", "a rounded arch sitting on the bottom line"],
  n: ["valley", "a rounded dip hanging from the top line"],
  l: ["corner", "down the left side, then along the bottom"],
  j: ["cane", "straight down with a little foot curling right"],
  w: ["vee", "down to a point and back up"],
};

const VOWEL_NAMES = {
  a: ["plain", "the stroke just ends — no ending at all"],
  i: ["flick", "a short tick kicked off the end"],
  e: ["wave", "a little S-curve rippling off the end"],
  o: ["circle", "a small closed loop drawn at the end"],
  u: ["hook", "an open curl that never closes"],
};

const STATS_KEY = "sitelen-nasin-stats-v1";

function loadStats() {
  try {
    return JSON.parse(localStorage.getItem(STATS_KEY)) || {};
  } catch {
    return {};
  }
}

function saveStats(stats) {
  try {
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  } catch {
    // private mode etc. — practice still works, it just doesn't persist
  }
}

// ---------------------------------------------------------------- learn tab

function ReferenceChart({ selected, onSelect }) {
  const cols = VOWELS.split("");
  const rows = CONSONANTS.split("");
  return (
    <div className="sn-chartbox">
      <div
        className="sn-chart-grid"
        style={{ gridTemplateColumns: `24px repeat(${cols.length}, minmax(0, 1fr))` }}
      >
        <span />
        {cols.map((v) => (
          <span key={v} className="sn-chart-head">
            {v}
          </span>
        ))}
        {rows.map((c) => (
          <React.Fragment key={c}>
            <span className="sn-chart-head">{c}</span>
            {cols.map((v) => {
              const banned = ILLEGAL_SYLLABLES.includes(c + v);
              const spec = { cons: c, vowel: v, coda: false };
              const isSel =
                selected && !selected.carrier && selected.cons === c && selected.vowel === v;
              return (
                <button
                  key={v}
                  type="button"
                  className={`sn-chartcell${banned ? " banned" : ""}${isSel ? " selected" : ""}`}
                  disabled={banned}
                  onClick={() => onSelect(spec)}
                  aria-label={banned ? `${c + v} (never occurs)` : `syllable ${c + v}`}
                >
                  <SyllableGlyph
                    spec={spec}
                    scale={0.62}
                    color={banned ? "#c5cabf" : undefined}
                  />
                  <span className="sn-latin">{banned ? "—" : c + v}</span>
                </button>
              );
            })}
          </React.Fragment>
        ))}
        <span className="sn-chart-head">–</span>
        {cols.map((v) => {
          const spec = { carrier: true, vowel: v, coda: false };
          const isSel = selected && selected.carrier && selected.vowel === v;
          return (
            <button
              key={v}
              type="button"
              className={`sn-chartcell${isSel ? " selected" : ""}`}
              onClick={() => onSelect(spec)}
              aria-label={`bare vowel ${v}`}
            >
              <SyllableGlyph spec={spec} scale={0.62} />
              <span className="sn-latin">{v}</span>
            </button>
          );
        })}
      </div>
      <p className="sn-hint">
        greyed syllables (ji, ti, wo, wu) never occur in toki pona. bottom row: carrier-dash
        forms for bare vowels. click any cell to break it down.
      </p>
    </div>
  );
}

function AnatomyPanel({ spec }) {
  const latin = strokeToLatin(spec);
  return (
    <div className="sn-anatomy">
      <SyllableGlyph spec={spec} scale={1.3} strokeWidth={3} />
      <div className="sn-anatomy-info">
        {spec.carrier ? (
          <>
            <b className="sn-mono">{latin}</b> rides a <b>carrier dash</b> — a short horizontal
            line standing in for "no consonant" — with the <b>{VOWEL_NAMES[spec.vowel][0]}</b>{" "}
            ending for <span className="sn-mono">{spec.vowel}</span>:{" "}
            {VOWEL_NAMES[spec.vowel][1]}.
          </>
        ) : (
          <>
            <b className="sn-mono">{latin}</b> = the <b>{CONS_NAMES[spec.cons][0]}</b> gesture
            for <span className="sn-mono">{spec.cons}</span> ({CONS_NAMES[spec.cons][1]}),
            finished with the <b>{VOWEL_NAMES[spec.vowel][0]}</b> ending for{" "}
            <span className="sn-mono">{spec.vowel}</span> ({VOWEL_NAMES[spec.vowel][1]}). One
            pen stroke, no lifting.
          </>
        )}
      </div>
    </div>
  );
}

function LearnTab() {
  const [selected, setSelected] = useState({ cons: "t", vowel: "o", coda: false });
  return (
    <>
      <div className="sn-panel">
        <h2>how the script works</h2>
        <p className="sn-prose">
          sitelen nasin writes toki pona <em>by sound</em>, one pen stroke per syllable. Every
          syllable is a <b>consonant gesture</b> (nine of them) finished with a{" "}
          <b>vowel ending</b> (five of them) — so the whole language fits in a 9×5 chart.
          Syllable strokes stack top-to-bottom into <b>blocks</b> of up to three; a long word
          spills into a second block joined by a faint dash.
        </p>
        <p className="sn-prose">
          Read a block top band to bottom band, then move right to the next block. Read a
          stroke the way the pen drew it: name the gesture, then the ending.
        </p>
        <h3>the 45 syllables</h3>
        <ReferenceChart selected={selected} onSelect={setSelected} />
        <AnatomyPanel spec={selected} />
        <h3>the extras</h3>
        <p className="sn-prose">
          Three special rules cover everything the chart doesn't:
        </p>
        <div className="sn-examples">
          <div className="sn-example">
            <WordGlyph word="kon" scale={0.8} />
            <span className="sn-latin">kon</span>
            <span className="sn-example-caption">
              final <b>-n</b> = a backward flick fused onto the stroke
            </span>
          </div>
          <div className="sn-example">
            <WordGlyph word="akesi" scale={0.8} />
            <span className="sn-latin">akesi</span>
            <span className="sn-example-caption">
              word-initial <b>a-</b> = an entry loop on the next stroke
            </span>
          </div>
          <div className="sn-example">
            <WordGlyph word="esun" scale={0.8} />
            <span className="sn-latin">esun</span>
            <span className="sn-example-caption">
              other bare vowels ride a <b>carrier dash</b>
            </span>
          </div>
          <div className="sn-example">
            <WordGlyph word="kijetesantakalu" scale={0.8} />
            <span className="sn-latin">kijetesantakalu</span>
            <span className="sn-example-caption">
              long words spill into extra blocks, joined by a dash
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- read tab

const rng = Math.random;

function makeSyllableQuestion() {
  const deck = legalSyllables();
  const pick = deck[Math.floor(rng() * deck.length)];
  // Sometimes drill the -n coda so the backward flick gets practiced too.
  const answer = rng() < 0.25 ? { ...pick, coda: true } : pick;
  const choices = shuffleWith(
    [answer, ...syllableDistractors(answer, rng)].map(strokeToLatin),
    rng
  );
  return { kind: "syllable", answer, answerLatin: strokeToLatin(answer), choices };
}

function makeWordQuestion(pool) {
  const answer = pool[Math.floor(rng() * pool.length)];
  const choices = shuffleWith([answer, ...wordDistractors(answer, pool, rng)], rng);
  return { kind: "word", answer, answerLatin: answer, choices };
}

function ReadTab() {
  const wordPool = useMemo(
    () => Object.keys(WORDS).filter((w) => isValidWord(w)),
    []
  );
  const [mode, setMode] = useState("syllable");
  const [question, setQuestion] = useState(() => makeSyllableQuestion());
  const [picked, setPicked] = useState(null);
  const [streak, setStreak] = useState(0);
  const [stats, setStats] = useState(loadStats);
  const timerRef = useRef(null);

  const next = useCallback(
    (m = mode) => {
      clearTimeout(timerRef.current);
      setPicked(null);
      setQuestion(m === "syllable" ? makeSyllableQuestion() : makeWordQuestion(wordPool));
    },
    [mode, wordPool]
  );

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const switchMode = (m) => {
    setMode(m);
    setStreak(0);
    next(m);
  };

  const answered = picked !== null;
  const modeStats = stats[mode] || { best: 0, seen: 0, correct: 0 };

  const choose = (choice) => {
    if (answered) return;
    setPicked(choice);
    const right = choice === question.answerLatin;
    const nextStreak = right ? streak + 1 : 0;
    setStreak(nextStreak);
    const s = {
      ...stats,
      [mode]: {
        best: Math.max(modeStats.best, nextStreak),
        seen: modeStats.seen + 1,
        correct: modeStats.correct + (right ? 1 : 0),
      },
    };
    setStats(s);
    saveStats(s);
    if (right) timerRef.current = setTimeout(() => next(), 1000);
  };

  const gloss = question.kind === "word" ? WORDS[question.answer]?.g : null;

  return (
    <div className="sn-panel">
      <div className="sn-modes">
        <button
          type="button"
          className={`sn-tab${mode === "syllable" ? " active" : ""}`}
          onClick={() => switchMode("syllable")}
        >
          syllables
        </button>
        <button
          type="button"
          className={`sn-tab${mode === "word" ? " active" : ""}`}
          onClick={() => switchMode("word")}
        >
          whole words
        </button>
      </div>
      <div className="sn-stats">
        <span>streak {streak}</span>
        <span>best {Math.max(modeStats.best, streak)}</span>
        <span>
          {modeStats.correct}/{modeStats.seen} all-time
        </span>
      </div>

      <GlyphCard>
        {question.kind === "syllable" ? (
          <SyllableGlyph spec={question.answer} scale={1.4} strokeWidth={3} />
        ) : (
          <WordGlyph word={question.answer} scale={1.1} />
        )}
      </GlyphCard>

      <div className="sn-choices">
        {question.choices.map((c) => {
          let cls = "sn-choice";
          if (answered) {
            if (c === question.answerLatin) cls += " correct";
            else if (c === picked) cls += " wrong";
            else cls += " dim";
          }
          return (
            <button key={c} type="button" className={cls} disabled={answered} onClick={() => choose(c)}>
              {c}
            </button>
          );
        })}
      </div>

      <div className="sn-feedback">
        {answered &&
          (picked === question.answerLatin ? (
            <span className="yay">nasin pona! </span>
          ) : (
            <span className="nope">
              it reads <b className="sn-mono">{question.answerLatin}</b>.{" "}
            </span>
          ))}
        {answered && gloss && (
          <span className="gloss">
            {question.answer} — {gloss}
          </span>
        )}
      </div>

      {answered && picked !== question.answerLatin && (
        <button type="button" className="sn-btn" onClick={() => next()}>
          next
        </button>
      )}
      {!answered && (
        <button type="button" className="sn-btn ghost" onClick={() => next()}>
          skip
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- write tab

function WriteTab() {
  const [text, setText] = useState("toki! sina wile ala wile moku? mi wile e kili suwi.");
  const [scale, setScale] = useState(0.9);
  const [stroke, setStroke] = useState(3);
  const [boxes, setBoxes] = useState(false);
  const [labels, setLabels] = useState(true);
  return (
    <div className="sn-panel">
      <h2>write something</h2>
      <div className="sn-board">
        {text.trim() ? (
          <ScriptText text={text} scale={scale} strokeWidth={stroke} labels={labels} boxes={boxes} />
        ) : (
          <div className="sn-empty">type some toki pona below to see it written</div>
        )}
      </div>
      <div className="sn-inputrow">
        <label className="sn-label" htmlFor="sn-txt">
          toki pona text
        </label>
        <textarea
          id="sn-txt"
          spellCheck={false}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      <div className="sn-controls">
        <div className="sn-ctrl">
          <label className="sn-label" htmlFor="sn-size">
            size
          </label>
          <input
            id="sn-size"
            type="range"
            min="0.5"
            max="1.6"
            step="0.05"
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
          />
        </div>
        <div className="sn-ctrl">
          <label className="sn-label" htmlFor="sn-sw">
            stroke weight
          </label>
          <input
            id="sn-sw"
            type="range"
            min="2"
            max="7"
            step="0.5"
            value={stroke}
            onChange={(e) => setStroke(parseFloat(e.target.value))}
          />
        </div>
        <label className="sn-toggle">
          <input type="checkbox" checked={boxes} onChange={(e) => setBoxes(e.target.checked)} />
          block outlines
        </label>
        <label className="sn-toggle">
          <input type="checkbox" checked={labels} onChange={(e) => setLabels(e.target.checked)} />
          latin labels
        </label>
      </div>
      <p className="sn-hint">
        words in red aren't valid toki pona phonology. punctuation passes through as-is.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- page

const TABS = [
  { id: "learn", label: "learn" },
  { id: "read", label: "practice reading" },
  { id: "write", label: "write" },
];

export default function SitelenNasin() {
  const [tab, setTab] = useState("learn");
  return (
    <div className="sn-root">
      <div className="sn-wrap">
        <header className="sn-header">
          <h1>sitelen nasin</h1>
          <span className="sn-sub">toki pona block script · one stroke per syllable</span>
          <Link className="sn-back" to="/sillyprojects">
            ← silly projects
          </Link>
        </header>
        <nav className="sn-tabs" aria-label="sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`sn-tab${tab === t.id ? " active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>
        {tab === "learn" && <LearnTab />}
        {tab === "read" && <ReadTab />}
        {tab === "write" && <WriteTab />}
      </div>
    </div>
  );
}
